import api from "lib/axios";
import { ApiEnvelope, unwrapData } from "services/apiTypes";

export type HomeBannerActionType = "none" | "external_url" | "in_app" | "course" | "teacher";

export type HomeBannerCourseOption = {
  id: number;
  title: string;
  subject?: string | null;
  instructor?: string | null;
};

export type HomeBannerTeacherOption = {
  id: number;
  name: string;
  subject_id: number;
  subject?: string | null;
  courses_count: number;
  target: string;
};

export type AdminHomeBanner = {
  id: number;
  title_ar?: string | null;
  title_en?: string | null;
  subtitle_ar?: string | null;
  subtitle_en?: string | null;
  image: string;
  image_url?: string | null;
  action_type: HomeBannerActionType;
  action_value?: string | null;
  sort_order: number;
  is_active: boolean;
  starts_at?: string | null;
  ends_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

export type HomeBannerPayload = {
  title_ar?: string | null;
  title_en?: string | null;
  subtitle_ar?: string | null;
  subtitle_en?: string | null;
  image?: string;
  action_type: HomeBannerActionType;
  action_value?: string | null;
  sort_order: number;
  is_active: boolean;
};

export const homeBannersService = {
  async list(): Promise<AdminHomeBanner[]> {
    const response = await api.get<ApiEnvelope<{ data: AdminHomeBanner[] }>>(
      "/mcp/home-banners",
      { meta: { toastSuccess: false } },
    );
    return unwrapData<AdminHomeBanner[]>(response.data) ?? [];
  },

  async courseOptions(search = ""): Promise<HomeBannerCourseOption[]> {
    const response = await api.get<ApiEnvelope<{ data: HomeBannerCourseOption[] }>>(
      "/mcp/home-banners/course-options",
      { params: { search: search.trim() || undefined }, meta: { toastSuccess: false } },
    );
    return unwrapData<HomeBannerCourseOption[]>(response.data) ?? [];
  },

  async teacherOptions(search = ""): Promise<HomeBannerTeacherOption[]> {
    const response = await api.get<ApiEnvelope<{ data: HomeBannerTeacherOption[] }>>(
      "/mcp/home-banners/teacher-options",
      { params: { search: search.trim() || undefined }, meta: { toastSuccess: false } },
    );
    return unwrapData<HomeBannerTeacherOption[]>(response.data) ?? [];
  },

  async create(payload: HomeBannerPayload): Promise<AdminHomeBanner | null> {
    const response = await api.post<ApiEnvelope<{ data: AdminHomeBanner }>>(
      "/mcp/home-banners",
      payload,
    );
    return unwrapData<AdminHomeBanner>(response.data);
  },

  async update(id: number, payload: HomeBannerPayload): Promise<AdminHomeBanner | null> {
    const response = await api.put<ApiEnvelope<{ data: AdminHomeBanner }>>(
      `/mcp/home-banners/${id}`,
      payload,
    );
    return unwrapData<AdminHomeBanner>(response.data);
  },

  async remove(id: number): Promise<void> {
    await api.delete(`/mcp/home-banners/${id}`);
  },
};
